import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Tab, Tabs, Modal, Form, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import OrgPrintChrome from './OrgPrintChrome';
import SearchableSelect from './SearchableSelect';
import { useAuth } from '../contexts/AuthContext';
import { formatDate, formatDateTime } from '../utils/dateFormat';

const REQUEST_OUTCOMES = {
  APPROVED: { label: 'Approved (private to them)' },
  LINKED: { label: 'Approved and filed under a parent item' },
  MADE_GLOBAL: { label: 'Made available to everyone' },
  MERGED: { label: 'Merged into a shared item' },
  REMOVED: { label: 'Removed' }
};
const USED_AS_LABELS = { RAW_MATERIAL: 'Raw material', PRODUCT: 'Product', BOTH: 'Raw material and product', NOT_USED_YET: 'Not used yet' };

const ATTR_DATA_TYPES = [
  { value: 'TEXT', label: 'Text' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'BOOLEAN', label: 'Yes / No' },
  { value: 'DATE', label: 'Date' },
  { value: 'SELECT', label: 'Select (dropdown)' }
];

function MaterialMaster() {
  // Tech Support reviews and tidies the Global Material Master; creating, deleting and defining attributes or grades is System Admin's
  const { user } = useAuth();
  const canCurate = !!user?.isSystemAdmin;
  const location = useLocation();
  const [loading, setLoading] = useState(true);

  const storageKey = `activeTab_${location.pathname}_MaterialMaster`;
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved === 'attributes' ? 'items' : (saved || 'items');
  });

  useEffect(() => {
    localStorage.setItem(storageKey, activeTab);
  }, [activeTab, storageKey]);

  const [itemTypes, setItemTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItemTypeFilter, setSelectedItemTypeFilter] = useState('ALL');
  const [verifiedFilter, setVerifiedFilter] = useState('ALL'); // 'ALL' | 'PENDING' | 'VERIFIED'
  // Categories tab: words typed, whose they are ('ALL', 'SHARED' or an organization's id) and whether they are active
  const [typeSearch, setTypeSearch] = useState('');
  const [typeOwner, setTypeOwner] = useState('ALL');
  const [typeStatus, setTypeStatus] = useState('ALL');

  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyTargetItem, setVerifyTargetItem] = useState(null);
  const [verifyFormData, setVerifyFormData] = useState({ gtin: '', hsCode: '', itemTypeId: '' });
  const [savingVerify, setSavingVerify] = useState(false);

  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeTargetItem, setMergeTargetItem] = useState(null);
  const [mergeCanonicalId, setMergeCanonicalId] = useState('');
  const [savingMerge, setSavingMerge] = useState(false);
  const [showTypeOfModal, setShowTypeOfModal] = useState(false);
  const [typeOfTarget, setTypeOfTarget] = useState(null);
  const [typeOfGenericId, setTypeOfGenericId] = useState('');
  const [savingTypeOf, setSavingTypeOf] = useState(false);

  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [printType, setPrintType] = useState(null);

  const [showItemTypeModal, setShowItemTypeModal] = useState(false);
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [itemTypeFormData, setItemTypeFormData] = useState({
    name: '',
    description: '',
    isActive: true,
    parentId: ''
  });

  const [showItemModal, setShowItemModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemFormData, setItemFormData] = useState({
    name: '',
    gtin: '',
    hsCode: '',
    sku: '',
    description: '',
    itemTypeId: '',
    isActive: true,
    attributes: {}
  });
  const [itemTypeAttributes, setItemTypeAttributes] = useState([]);

  // Manage attributes for a specific category
  const [attrTypeContext, setAttrTypeContext] = useState(null);
  const [typeAttributeDefs, setTypeAttributeDefs] = useState([]);
  const [showAttrManagerModal, setShowAttrManagerModal] = useState(false);
  const [showAttrFormModal, setShowAttrFormModal] = useState(false);
  const [selectedAttr, setSelectedAttr] = useState(null);
  const [attrFormData, setAttrFormData] = useState({
    code: '',
    name: '',
    description: '',
    dataType: 'TEXT',
    optionsText: '',
    unit: '',
    displayOrder: 0,
    isActive: true
  });

  // Manage quality grades for a specific category
  const [gradeTypeContext, setGradeTypeContext] = useState(null);
  const [typeGrades, setTypeGrades] = useState([]);
  const [showGradeManagerModal, setShowGradeManagerModal] = useState(false);
  const [showGradeFormModal, setShowGradeFormModal] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [gradeFormData, setGradeFormData] = useState({
    code: '',
    label: '',
    description: '',
    sortOrder: 0,
    isActive: true
  });

  useEffect(() => {
    if (activeTab === 'item-types') {
      fetchItemTypes();
    } else if (activeTab === 'requests') {
      fetchRequests();
      if (itemTypes.length === 0) fetchItemTypes();
    } else {
      fetchItems();
      if (itemTypes.length === 0) fetchItemTypes();
    }
  }, [activeTab]);

  useEffect(() => {
    filterItems();
  }, [items, searchTerm, selectedItemTypeFilter, verifiedFilter]);

  // The categories to list: those matching the words and filters, each under its parents (kept above a match so it shows where it sits)
  const visibleItemTypes = () => {
    const words = typeSearch.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const byId = new Map(itemTypes.map((t) => [t.id, t]));
    const matches = (t) => {
      if (typeOwner === 'SHARED' && t.organization) return false;
      if (typeOwner !== 'ALL' && typeOwner !== 'SHARED' && String(t.organization?.id) !== typeOwner) return false;
      if (typeStatus === 'ACTIVE' && !t.isActive) return false;
      if (typeStatus === 'INACTIVE' && t.isActive) return false;
      const hay = `${t.name} ${t.parent?.name || ''} ${t.description || ''} ${t.organization?.name || ''}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    };
    const keep = new Set();
    const matched = itemTypes.filter(matches);
    matched.forEach((t) => {
      keep.add(t.id);
      for (let p = t.parent && byId.get(t.parent.id); p && !keep.has(p.id); p = p.parent && byId.get(p.parent.id)) keep.add(p.id);
    });
    const matchedIds = new Set(matched.map((t) => t.id));
    return { rows: buildItemTypeHierarchy(itemTypes.filter((t) => keep.has(t.id))), matchedIds, count: matched.length };
  };

  const fetchItemTypes = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/system-admin/material-master/item-types');
      setItemTypes(response.data || []);
    } catch (error) {
      toast.error('Error fetching categories');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Item Requests: drafts organizations asked for that wait for a decision, and what was decided before
  const [requests, setRequests] = useState({ pending: [], decided: [] });
  const [requestSearch, setRequestSearch] = useState('');
  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/system-admin/material-master/requests');
      setRequests({ pending: response.data?.pending || [], decided: response.data?.decided || [] });
    } catch (error) {
      toast.error('Error fetching item requests');
    } finally {
      setLoading(false);
    }
  };
  // After a decision, whichever list the person is looking at is read again
  const refreshAfterDecision = () => { fetchItems(); fetchRequests(); };

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/system-admin/material-master/items');
      setItems(response.data || []);
      setFilteredItems(response.data || []);
    } catch (error) {
      toast.error('Error fetching items');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttributesForType = useCallback(async (itemTypeId, activeOnly = false) => {
    if (!itemTypeId) {
      setItemTypeAttributes([]);
      return [];
    }
    try {
      const path = activeOnly
        ? `/api/system-admin/material-master/item-types/${itemTypeId}/attributes/active`
        : `/api/system-admin/material-master/item-types/${itemTypeId}/attributes`;
      const response = await axios.get(path);
      return response.data || [];
    } catch (error) {
      toast.error('Error fetching attributes for category');
      console.error(error);
      return [];
    }
  }, []);

  const filterItems = () => {
    let filtered = items;
    if (verifiedFilter === 'PENDING') {
      filtered = filtered.filter(item => item.verified === false);
    } else if (verifiedFilter === 'VERIFIED') {
      filtered = filtered.filter(item => item.verified !== false);
    }
    if (selectedItemTypeFilter !== 'ALL') {
      filtered = filtered.filter(item =>
        item.itemType && item.itemType.id.toString() === selectedItemTypeFilter
      );
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(term) ||
        (item.gtin && item.gtin.toLowerCase().includes(term)) ||
        (item.hsCode && item.hsCode.toLowerCase().includes(term)) ||
        (item.sku && item.sku.toLowerCase().includes(term)) ||
        (item.description && item.description.toLowerCase().includes(term))
      );
    }
    setFilteredItems(filtered);
  };

  const emptyAttributesMap = (defs) => {
    const map = {};
    (defs || []).forEach((def) => {
      map[def.code] = def.dataType === 'BOOLEAN' ? false : '';
    });
    return map;
  };

  const handleCreateItemType = () => {
    setItemTypeFormData({ name: '', description: '', isActive: true, parentId: '' });
    setSelectedItemType(null);
    setShowItemTypeModal(true);
  };

  const handleEditItemType = (itemType) => {
    setItemTypeFormData({
      name: itemType.name,
      description: itemType.description || '',
      isActive: itemType.isActive,
      parentId: itemType.parent?.id ? itemType.parent.id.toString() : ''
    });
    setSelectedItemType(itemType);
    setShowItemTypeModal(true);
  };

  // Candidate parents for the type being created/edited: everything except itself and its own
  // direct children (deeper cycles are still caught server-side, but this keeps the obviously
  // wrong choices out of the dropdown).
  const parentOptionsFor = (itemType) => {
    if (!itemType) return itemTypes;
    return itemTypes.filter((t) => t.id !== itemType.id && t.parent?.id !== itemType.id);
  };

  // Flattens the parent/child relationships into a depth-first, indentable list for the table -
  // each top-level type immediately followed by its children, recursively.
  const buildItemTypeHierarchy = (types) => {
    const byParentId = {};
    types.forEach((t) => {
      const key = t.parent?.id ?? 'root';
      (byParentId[key] = byParentId[key] || []).push(t);
    });
    const result = [];
    const visit = (parentKey, depth) => {
      (byParentId[parentKey] || [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach((t) => {
          result.push({ itemType: t, depth });
          visit(t.id, depth + 1);
        });
    };
    visit('root', 0);
    return result;
  };

  // Categories as options for a searchable picker: the name, where it sits (its parents) under it, and the description to search by.
  // Sorted by that whole path, so a category comes right after its parent.
  const typeOptions = (types) => {
    const byId = new Map(itemTypes.map((t) => [t.id, t]));
    const pathOf = (t) => {
      const names = [];
      for (let p = t.parent && byId.get(t.parent.id); p; p = p.parent && byId.get(p.parent.id)) names.unshift(p.name);
      return names.join(' › ');
    };
    return types
      .map((t) => {
        const path = pathOf(t);
        return { value: String(t.id), label: t.name + (t.organization ? ' (theirs)' : ''), subtitle: path, keywords: t.description || '', sort: path ? `${path} › ${t.name}` : t.name };
      })
      .sort((a, b) => a.sort.localeCompare(b.sort));
  };

  const handleManageAttributes = async (itemType) => {
    setAttrTypeContext(itemType);
    const defs = await fetchAttributesForType(itemType.id, false);
    setTypeAttributeDefs(defs);
    setShowAttrManagerModal(true);
  };

  const refreshTypeAttributes = async () => {
    if (!attrTypeContext?.id) return;
    const defs = await fetchAttributesForType(attrTypeContext.id, false);
    setTypeAttributeDefs(defs);
  };

  const fetchGradesForType = useCallback(async (itemTypeId) => {
    if (!itemTypeId) return [];
    try {
      const response = await axios.get(
        `/api/system-admin/material-master/item-types/${itemTypeId}/grades`
      );
      return response.data || [];
    } catch (error) {
      toast.error('Error fetching quality grades for category');
      return [];
    }
  }, []);

  const handleManageGrades = async (itemType) => {
    setGradeTypeContext(itemType);
    const grades = await fetchGradesForType(itemType.id);
    setTypeGrades(grades);
    setShowGradeManagerModal(true);
  };

  const refreshTypeGrades = async () => {
    if (!gradeTypeContext?.id) return;
    const grades = await fetchGradesForType(gradeTypeContext.id);
    setTypeGrades(grades);
  };

  const handleCreateGrade = () => {
    setGradeFormData({
      code: '',
      label: '',
      description: '',
      sortOrder: typeGrades.length,
      isActive: true
    });
    setSelectedGrade(null);
    setShowGradeFormModal(true);
  };

  const handleEditGrade = (grade) => {
    setGradeFormData({
      code: grade.code || '',
      label: grade.label || '',
      description: grade.description || '',
      sortOrder: grade.sortOrder != null ? grade.sortOrder : 0,
      isActive: grade.isActive !== false
    });
    setSelectedGrade(grade);
    setShowGradeFormModal(true);
  };

  const handleDeleteGrade = async (id, isSystem) => {
    if (isSystem) {
      toast.error('Cannot delete system grade');
      return;
    }
    if (!window.confirm('Delete this quality grade from the category?')) return;
    try {
      await axios.delete(`/api/system-admin/material-master/grades/${id}`);
      toast.success('Quality grade deleted');
      refreshTypeGrades();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error deleting quality grade');
    }
  };

  const handleSaveGrade = async (e) => {
    e.preventDefault();
    if (!gradeTypeContext?.id) return;
    try {
      const payload = {
        code: gradeFormData.code,
        label: gradeFormData.label,
        description: gradeFormData.description || null,
        sortOrder: parseInt(gradeFormData.sortOrder, 10) || 0,
        isActive: gradeFormData.isActive
      };
      if (selectedGrade) {
        await axios.put(`/api/system-admin/material-master/grades/${selectedGrade.id}`, payload);
        toast.success('Quality grade updated');
      } else {
        await axios.post(
          `/api/system-admin/material-master/item-types/${gradeTypeContext.id}/grades`,
          payload
        );
        toast.success('Quality grade created');
      }
      setShowGradeFormModal(false);
      refreshTypeGrades();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error saving quality grade');
    }
  };

  const handleViewPrint = (data, type) => {
    setPrintData(data);
    setPrintType(type);
    setShowPrintModal(true);
  };

  const handlePrint = () => window.print();

  const handleDeleteItemType = async (id, isSystem) => {
    if (isSystem) {
      toast.error('Cannot delete system category');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await axios.delete(`/api/system-admin/material-master/item-types/${id}`);
      toast.success('Category deleted successfully');
      fetchItemTypes();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error deleting category');
    }
  };

  const handleSaveItemType = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: itemTypeFormData.name,
        description: itemTypeFormData.description,
        isActive: itemTypeFormData.isActive,
        parent: itemTypeFormData.parentId ? { id: parseInt(itemTypeFormData.parentId, 10) } : null
      };
      if (selectedItemType) {
        await axios.put(`/api/system-admin/material-master/item-types/${selectedItemType.id}`, payload);
        toast.success('Category updated successfully');
      } else {
        await axios.post('/api/system-admin/material-master/item-types', payload);
        toast.success('Category created successfully. Use Attributes to define fields for this type.');
      }
      setShowItemTypeModal(false);
      fetchItemTypes();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error saving category');
    }
  };

  const applyItemTypeChange = async (itemTypeId, keepValues = {}) => {
    const defs = await fetchAttributesForType(itemTypeId, true);
    setItemTypeAttributes(defs);
    const next = emptyAttributesMap(defs);
    Object.keys(next).forEach((code) => {
      if (keepValues[code] !== undefined && keepValues[code] !== null && keepValues[code] !== '') {
        next[code] = keepValues[code];
      }
    });
    setItemFormData((prev) => ({
      ...prev,
      itemTypeId,
      attributes: next
    }));
  };

  const handleCreateItem = async () => {
    const defaultTypeId = itemTypes.length > 0 ? itemTypes[0].id.toString() : '';
    setSelectedItem(null);
    setItemFormData({
      name: '',
      gtin: '',
      hsCode: '',
      sku: '',
      description: '',
      itemTypeId: defaultTypeId,
      isActive: true,
      attributes: {}
    });
    setShowItemModal(true);
    if (defaultTypeId) {
      await applyItemTypeChange(defaultTypeId);
    } else {
      setItemTypeAttributes([]);
    }
  };

  const handleEditItem = async (item) => {
    const typeId = item.itemType ? item.itemType.id.toString() : '';
    setSelectedItem(item);
    setItemFormData({
      name: item.name,
      gtin: item.gtin || '',
      hsCode: item.hsCode || '',
      sku: item.sku || '',
      description: item.description || '',
      itemTypeId: typeId,
      isActive: item.isActive,
      attributes: item.attributes || {}
    });
    setShowItemModal(true);
    if (typeId) {
      await applyItemTypeChange(typeId, item.attributes || {});
    } else {
      setItemTypeAttributes([]);
    }
  };

  const handleDeleteItem = async (id, isSystem) => {
    if (isSystem) {
      toast.error('Cannot delete system item');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await axios.delete(`/api/system-admin/material-master/items/${id}`);
      toast.success('Item deleted successfully');
      refreshAfterDecision();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error deleting item');
    }
  };

  const handleMakeItemGlobal = async (item) => {
    const type = item.itemType?.organization ? ` Its category "${item.itemType.name}" and the category's attributes become available to everyone too.` : '';
    if (!window.confirm(`Make "${item.name}" available to every organization?${type} ${item.createdByOrganization?.name || 'The organization'} keeps using it as before, but can no longer edit it. Check the name first.`)) return;
    try {
      await axios.post(`/api/system-admin/material-master/items/${item.id}/make-global`);
      toast.success('Now available to everyone');
      refreshAfterDecision();
      fetchItemTypes();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not make the item available to everyone');
    }
  };

  const handleMakeTypeGlobal = async (itemType) => {
    if (!window.confirm(`Make the category "${itemType.name}" and its attributes available to every organization? ${itemType.organization?.name || 'The organization'} keeps using it as before. Check the names first.`)) return;
    try {
      await axios.post(`/api/system-admin/material-master/item-types/${itemType.id}/make-global`);
      toast.success('Now available to everyone');
      fetchItemTypes();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not make the category available to everyone');
    }
  };

  const handleOpenVerify = (item) => {
    setVerifyTargetItem(item);
    setVerifyFormData({ gtin: item.gtin || '', hsCode: item.hsCode || '', itemTypeId: item.itemType?.id ? String(item.itemType.id) : '' });
    if (itemTypes.length === 0) fetchItemTypes();
    setShowVerifyModal(true);
  };

  const handleSaveVerify = async (e) => {
    e.preventDefault();
    setSavingVerify(true);
    try {
      await axios.post(`/api/system-admin/material-master/items/${verifyTargetItem.id}/verify`, {
        gtin: verifyFormData.gtin || null,
        hsCode: verifyFormData.hsCode || null,
        // Only when it was changed, so approving as it is never touches the category
        itemTypeId: String(verifyTargetItem.itemType?.id) !== verifyFormData.itemTypeId ? verifyFormData.itemTypeId : null
      });
      toast.success('Item approved');
      setShowVerifyModal(false);
      refreshAfterDecision();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error verifying item');
    } finally {
      setSavingVerify(false);
    }
  };

  const handleOpenTypeOf = (item) => {
    setTypeOfTarget(item);
    setTypeOfGenericId('');
    setShowTypeOfModal(true);
  };

  const handleSaveTypeOf = async (e) => {
    e.preventDefault();
    if (!typeOfGenericId) return;
    setSavingTypeOf(true);
    try {
      await axios.post(`/api/system-admin/material-master/items/${typeOfTarget.id}/type-of/${typeOfGenericId}`);
      toast.success('Approved and filed under the parent item');
      setShowTypeOfModal(false);
      refreshAfterDecision();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not file the item under the parent item');
    } finally {
      setSavingTypeOf(false);
    }
  };

  const handleOpenMerge = (item) => {
    setMergeTargetItem(item);
    setMergeCanonicalId('');
    setShowMergeModal(true);
  };

  const handleSaveMerge = async (e) => {
    e.preventDefault();
    if (!mergeCanonicalId) return;
    setSavingMerge(true);
    try {
      await axios.post(`/api/system-admin/material-master/items/${mergeTargetItem.id}/merge-into/${mergeCanonicalId}`);
      toast.success('Item merged into the canonical record');
      setShowMergeModal(false);
      refreshAfterDecision();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error merging item');
    } finally {
      setSavingMerge(false);
    }
  };

  const setAttributeValue = (code, value) => {
    setItemFormData((prev) => ({
      ...prev,
      attributes: { ...prev.attributes, [code]: value }
    }));
  };

  const buildAttributesPayload = () => {
    const out = {};
    Object.entries(itemFormData.attributes || {}).forEach(([code, value]) => {
      if (value === '' || value === null || value === undefined) return;
      const def = itemTypeAttributes.find((d) => d.code === code);
      if (def?.dataType === 'NUMBER') {
        const num = typeof value === 'number' ? value : parseFloat(value);
        if (!Number.isNaN(num)) out[code] = num;
      } else if (def?.dataType === 'BOOLEAN') {
        out[code] = Boolean(value);
      } else {
        out[code] = value;
      }
    });
    return out;
  };

  const handleSaveItem = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: itemFormData.name,
        gtin: itemFormData.gtin || null,
        hsCode: itemFormData.hsCode || null,
        sku: itemFormData.sku || null,
        description: itemFormData.description || null,
        isActive: itemFormData.isActive,
        itemType: { id: parseInt(itemFormData.itemTypeId, 10) },
        attributes: buildAttributesPayload()
      };
      if (selectedItem) {
        await axios.put(`/api/system-admin/material-master/items/${selectedItem.id}`, payload);
        toast.success('Item updated successfully');
      } else {
        await axios.post('/api/system-admin/material-master/items', payload);
        toast.success('Item created successfully');
      }
      setShowItemModal(false);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error saving item');
    }
  };

  const handleCreateAttr = () => {
    setAttrFormData({
      code: '',
      name: '',
      description: '',
      dataType: 'TEXT',
      optionsText: '',
      unit: '',
      displayOrder: typeAttributeDefs.length,
      isActive: true
    });
    setSelectedAttr(null);
    setShowAttrFormModal(true);
  };

  const handleEditAttr = (attr) => {
    setAttrFormData({
      code: attr.code || '',
      name: attr.name || '',
      description: attr.description || '',
      dataType: attr.dataType || 'TEXT',
      optionsText: (attr.options || []).join('\n'),
      unit: attr.unit || '',
      displayOrder: attr.displayOrder != null ? attr.displayOrder : 0,
      isActive: attr.isActive !== false
    });
    setSelectedAttr(attr);
    setShowAttrFormModal(true);
  };

  const handleDeleteAttr = async (id, isSystem) => {
    if (isSystem) {
      toast.error('Cannot delete system attribute');
      return;
    }
    if (!window.confirm('Delete this attribute from the category?')) return;
    try {
      await axios.delete(`/api/system-admin/material-master/attributes/${id}`);
      toast.success('Attribute deleted');
      refreshTypeAttributes();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error deleting attribute');
    }
  };

  const handleSaveAttr = async (e) => {
    e.preventDefault();
    if (!attrTypeContext?.id) return;
    try {
      const options = attrFormData.dataType === 'SELECT'
        ? attrFormData.optionsText.split('\n').map((s) => s.trim()).filter(Boolean)
        : [];
      const payload = {
        code: attrFormData.code,
        name: attrFormData.name,
        description: attrFormData.description || null,
        dataType: attrFormData.dataType,
        options,
        unit: attrFormData.unit || null,
        displayOrder: parseInt(attrFormData.displayOrder, 10) || 0,
        isActive: attrFormData.isActive
      };
      if (selectedAttr) {
        await axios.put(`/api/system-admin/material-master/attributes/${selectedAttr.id}`, payload);
        toast.success('Attribute updated');
      } else {
        await axios.post(
          `/api/system-admin/material-master/item-types/${attrTypeContext.id}/attributes`,
          payload
        );
        toast.success('Attribute created');
      }
      setShowAttrFormModal(false);
      refreshTypeAttributes();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error saving attribute');
    }
  };

  const formatAttrValue = (item, def) => {
    const val = item.attributes?.[def.code];
    if (val === null || val === undefined || val === '') return null;
    if (def.dataType === 'BOOLEAN') return val ? 'Yes' : 'No';
    if (def.unit) return `${val} ${def.unit}`;
    return String(val);
  };

  const renderAttributeInput = (def) => {
    const value = itemFormData.attributes?.[def.code];
    const label = def.unit ? `${def.name} (${def.unit})` : def.name;

    if (def.dataType === 'BOOLEAN') {
      return (
        <Form.Group className="mb-3" key={def.code}>
          <Form.Check
            type="switch"
            id={`attr-${def.code}`}
            label={label}
            checked={Boolean(value)}
            onChange={(e) => setAttributeValue(def.code, e.target.checked)}
          />
          {def.description && <Form.Text className="text-muted">{def.description}</Form.Text>}
        </Form.Group>
      );
    }

    if (def.dataType === 'SELECT') {
      return (
        <Form.Group className="mb-3" key={def.code}>
          <Form.Label>{label}</Form.Label>
          <Form.Select
            value={value ?? ''}
            onChange={(e) => setAttributeValue(def.code, e.target.value)}
          >
            <option value="">— Select —</option>
            {(def.options || []).map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </Form.Select>
          {def.description && <Form.Text className="text-muted">{def.description}</Form.Text>}
        </Form.Group>
      );
    }

    const inputType = def.dataType === 'NUMBER' ? 'number' : def.dataType === 'DATE' ? 'date' : 'text';
    return (
      <Form.Group className="mb-3" key={def.code}>
        <Form.Label>{label}</Form.Label>
        <Form.Control
          type={inputType}
          step={def.dataType === 'NUMBER' ? 'any' : undefined}
          value={value ?? ''}
          onChange={(e) => setAttributeValue(def.code, e.target.value)}
          placeholder={def.description || def.name}
        />
      </Form.Group>
    );
  };

  if (loading && itemTypes.length === 0 && items.length === 0) {
    return (
      <Container className="mt-4">
        <div className="d-flex justify-content-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Row className="mb-4">
        <Col>
          <p className="text-muted mb-0">
            Manage items and categories. Attribute definitions live on each Category; values are set on Items.
          </p>
        </Col>
      </Row>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
        <Tab eventKey="items" title="Items">
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h5 className="mb-0">Items Master</h5>
                {canCurate && <Button variant="primary" onClick={handleCreateItem}>+ Create Item</Button>}
              </div>
            </Card.Header>
            <Card.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <InputGroup>
                    <InputGroup.Text>🔍</InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search by name, GTIN, HS Code, SKU, or description..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                <Col md={4}>
                  <SearchableSelect
                    options={typeOptions(itemTypes.filter((it) => it.isActive))}
                    value={selectedItemTypeFilter === 'ALL' ? '' : selectedItemTypeFilter}
                    onChange={(typeId) => setSelectedItemTypeFilter(typeId || 'ALL')}
                    placeholder="All categories (type to search)"
                    emptyLabel="No matching category"
                  />
                </Col>
                <Col md={2}>
                  <div className="text-muted small">{filteredItems.length} item(s)</div>
                </Col>
              </Row>

              <div className="mb-3 d-flex gap-2">
                {['ALL', 'PENDING', 'VERIFIED'].map((f) => (
                  <Button
                    key={f}
                    size="sm"
                    variant={verifiedFilter === f ? 'primary' : 'outline-secondary'}
                    onClick={() => setVerifiedFilter(f)}
                  >
                    {f === 'ALL' ? 'All' : f === 'PENDING' ? 'Pending Review' : 'Verified'}
                    {f === 'PENDING' && ` (${items.filter((i) => i.verified === false).length})`}
                  </Button>
                ))}
              </div>

              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>GTIN</th>
                    <th>HS Code</th>
                    <th>SKU</th>
                    <th>Category</th>
                    <th>Attributes</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center text-muted">No items found</td>
                    </tr>
                  ) : (
                    filteredItems.map((item) => {
                      const attrEntries = Object.entries(item.attributes || {}).filter(
                        ([, v]) => v !== null && v !== undefined && v !== ''
                      );
                      const summary = attrEntries.slice(0, 3).map(([k, v]) => `${k}: ${v}`).join(' · ');
                      return (
                        <tr key={item.id}>
                          <td><strong>{item.name}</strong></td>
                          <td><code>{item.gtin || '-'}</code></td>
                          <td><code>{item.hsCode || '-'}</code></td>
                          <td><code>{item.sku || '-'}</code></td>
                          <td><Badge bg="info">{item.itemType?.name || '-'}</Badge></td>
                          <td className="small text-muted">{summary || '—'}</td>
                          <td>
                            {item.isActive
                              ? <Badge bg="success">Active</Badge>
                              : <Badge bg="danger">Inactive</Badge>}
                            {item.isSystem && <Badge bg="warning" className="ms-1">System</Badge>}
                            {item.verified !== false && item.createdByOrganization?.name && (
                              <div className="mt-1">
                                <Badge bg="secondary">Private to {item.createdByOrganization.name}</Badge>
                                {item.usedAs && <Badge bg="light" text="dark" className="ms-1">{USED_AS_LABELS[item.usedAs]}</Badge>}
                              </div>
                            )}
                            {item.verified === false && (
                              <div className="mt-1">
                                <Badge bg="warning" text="dark">Pending Review</Badge>
                                {item.usedAs && <Badge bg="light" text="dark" className="ms-1">{USED_AS_LABELS[item.usedAs]}</Badge>}
                                {item.createdByOrganization?.name && (
                                  <div className="small text-muted mt-1">
                                    Requested by {item.createdByOrganization.name}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="text-nowrap">
                            <Button size="sm" variant="outline-info" className="me-1" onClick={() => handleViewPrint(item, 'ITEM')}>View</Button>
                            <Button size="sm" variant="outline-primary" className="me-1" onClick={() => handleEditItem(item)}>Edit</Button>
                            {item.verified === false && (
                              <>
                                <Button size="sm" variant="outline-success" className="me-1" onClick={() => handleOpenVerify(item)} title="Reviewed and fine: it stays private to the organization that made it">Approve (keep private)</Button>
                                <Button size="sm" variant="outline-warning" className="me-1" onClick={() => handleOpenMerge(item)}>Merge into…</Button>
                              </>
                            )}
                            {item.createdByOrganization && (
                              <Button size="sm" variant="outline-success" className="me-1" onClick={() => handleMakeItemGlobal(item)}>Make available to everyone</Button>
                            )}
                            {!item.isSystem && canCurate && (
                              <Button size="sm" variant="outline-danger" onClick={() => handleDeleteItem(item.id, item.isSystem)}>Delete</Button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="requests" title={`Item Requests${requests.pending.length ? ` (${requests.pending.length})` : ''}`}>
          <Card className="mb-4">
            <Card.Header><h5 className="mb-0">Waiting for review</h5></Card.Header>
            <Card.Body>
              <p className="text-muted small">
                Materials organizations made because they could not find theirs. Approve it as it is (it stays private to them), merge it into a shared item,
                or make it available to everyone. The organization's Org Admin is told what you decided.
              </p>
              <Table striped bordered hover responsive>
                <thead>
                  <tr><th>Name</th><th>Category</th><th>HS Code</th><th>Requested by</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {requests.pending.length === 0 ? (
                    <tr><td colSpan="5" className="text-center text-muted">Nothing is waiting</td></tr>
                  ) : requests.pending.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <strong>{item.name}</strong>
                        {item.genericItemName && <div className="small text-muted">filed under {item.genericItemName}</div>}
                        {item.usedAs && <Badge bg="light" text="dark" className="ms-1">{USED_AS_LABELS[item.usedAs]}</Badge>}
                      </td>
                      <td><Badge bg="info">{item.itemType?.name || '-'}</Badge></td>
                      <td><code>{item.hsCode || '-'}</code></td>
                      <td className="small">
                        {item.createdByOrganization?.name}
                        {item.createdByEmail && <div className="text-muted">{item.createdByEmail}</div>}
                        {item.createdAt && <div className="text-muted">{formatDate(item.createdAt)}</div>}
                      </td>
                      <td className="text-nowrap">
                        <Button size="sm" variant="outline-info" className="me-1" onClick={() => handleViewPrint(item, 'ITEM')}>View</Button>
                        {canCurate && <Button size="sm" variant="outline-primary" className="me-1" onClick={() => { handleEditItem(item); }}>Edit</Button>}
                        <Button size="sm" variant="outline-success" className="me-1" onClick={() => handleOpenVerify(item)} title="Reviewed and fine: it stays private to the organization that made it">Approve (keep private)</Button>
                        <Button size="sm" variant="outline-warning" className="me-1" onClick={() => handleOpenMerge(item)} title="The same thing as a shared item: joins them and deletes this one">Merge into…</Button>
                        <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => handleOpenTypeOf(item)} title="A size or brand of a parent item: approved, kept as its own material, filed under the parent item">File under a parent item…</Button>
                        <Button size="sm" variant="outline-success" className="me-1" onClick={() => handleMakeItemGlobal(item)}>Make available to everyone</Button>
                        {canCurate && <Button size="sm" variant="outline-danger" onClick={() => handleDeleteItem(item.id, item.isSystem)}>Delete</Button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h5 className="mb-0">Decided</h5>
                <InputGroup style={{ maxWidth: 320 }}>
                  <InputGroup.Text>🔍</InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search by item, organization or person..."
                    value={requestSearch}
                    onChange={(e) => setRequestSearch(e.target.value)}
                  />
                </InputGroup>
              </div>
            </Card.Header>
            <Card.Body>
              <Table striped bordered hover responsive>
                <thead>
                  <tr><th>Decided</th><th>Requested item</th><th>Organization</th><th>Outcome</th><th>Category</th><th>Decided by</th></tr>
                </thead>
                <tbody>
                  {(() => {
                    const q = requestSearch.trim().toLowerCase();
                    const rows = requests.decided.filter((d) => !q || [d.itemName, d.resultItemName, d.organizationName, d.requestedByEmail, d.decidedByEmail]
                      .some((v) => (v || '').toLowerCase().includes(q)));
                    if (rows.length === 0) {
                      return <tr><td colSpan="6" className="text-center text-muted">{requests.decided.length === 0 ? 'No decisions yet' : 'No matching decision'}</td></tr>;
                    }
                    return rows.map((d) => (
                      <tr key={d.id}>
                        <td className="text-nowrap small">{formatDateTime(d.decidedAt)}</td>
                        <td><strong>{d.itemName}</strong>{d.requestedByEmail && <div className="small text-muted">{d.requestedByEmail}</div>}</td>
                        <td>{d.organizationName || '-'}</td>
                        <td>
                          {REQUEST_OUTCOMES[d.decision]?.label || d.decision}
                          {d.decision === 'MERGED' && d.resultItemName && <div className="small">into <strong>{d.resultItemName}</strong></div>}
                        </td>
                        <td className="small">
                          {d.categoryAfter && d.categoryAfter !== d.categoryBefore
                            ? <>{d.categoryBefore || '-'} → <strong>{d.categoryAfter}</strong></>
                            : (d.categoryAfter || d.categoryBefore || '-')}
                          {d.hsCodeAfter && d.hsCodeAfter !== d.hsCodeBefore && <div className="text-muted">HSN {d.hsCodeBefore || '-'} → {d.hsCodeAfter}</div>}
                        </td>
                        <td className="small">{d.decidedByEmail || '-'}</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </Table>
              <div className="text-muted small">The latest 200 decisions.</div>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="item-types" title="Categories">
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Categories Master</h5>
                {canCurate && <Button variant="primary" onClick={handleCreateItemType}>+ Create Category</Button>}
              </div>
            </Card.Header>
            <Card.Body>
              {(() => {
                const owners = [...new Map(itemTypes.filter((t) => t.organization).map((t) => [t.organization.id, t.organization.name])).entries()]
                  .sort((a, b) => String(a[1]).localeCompare(String(b[1])));
                const shown = visibleItemTypes();
                return (
                  <Row className="g-2 mb-3 align-items-center">
                    <Col md={5}>
                      <InputGroup>
                        <InputGroup.Text>🔍</InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Search by name, parent, description or organization..."
                          value={typeSearch}
                          onChange={(e) => setTypeSearch(e.target.value)}
                        />
                      </InputGroup>
                    </Col>
                    <Col md={3}>
                      <Form.Select value={typeOwner} onChange={(e) => setTypeOwner(e.target.value)}>
                        <option value="ALL">Shared and private</option>
                        <option value="SHARED">Shared (Global Material Master)</option>
                        {owners.map(([id, name]) => <option key={id} value={String(id)}>Private to {name}</option>)}
                      </Form.Select>
                    </Col>
                    <Col md={2}>
                      <Form.Select value={typeStatus} onChange={(e) => setTypeStatus(e.target.value)}>
                        <option value="ALL">Any status</option>
                        <option value="ACTIVE">Active</option>
                        <option value="INACTIVE">Inactive</option>
                      </Form.Select>
                    </Col>
                    <Col md={2} className="text-muted small">{shown.count} of {itemTypes.length} categories</Col>
                  </Row>
                );
              })()}
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Parent</th>
                    <th>Description</th>
                    <th>Items Count</th>
                    <th>Status</th>
                    <th>System</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleItemTypes().rows.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center text-muted">{itemTypes.length === 0 ? 'No categories found' : 'No category matches. Try fewer words or clear the filters.'}</td>
                    </tr>
                  ) : (
                    visibleItemTypes().rows.map(({ itemType, depth }) => (
                      <tr key={itemType.id} className={typeSearch.trim() && !visibleItemTypes().matchedIds.has(itemType.id) ? 'text-muted' : ''}>
                        <td style={{ paddingLeft: `${12 + depth * 24}px` }}>
                          {depth > 0 && <span className="text-muted">↳ </span>}
                          <strong>{itemType.name}</strong>
                        </td>
                        <td className="text-muted">{itemType.parent?.name || '-'}</td>
                        <td>{itemType.description || '-'}</td>
                        <td>{itemType.items ? itemType.items.length : 0}</td>
                        <td>
                          {itemType.isActive
                            ? <Badge bg="success">Active</Badge>
                            : <Badge bg="danger">Inactive</Badge>}
                        </td>
                        <td>
                          {itemType.isSystem
                            ? <Badge bg="warning">System</Badge>
                            : <Badge bg="secondary">Custom</Badge>}
                          {itemType.organization?.name && (
                            <div className="mt-1"><Badge bg="secondary">Private to {itemType.organization.name}</Badge></div>
                          )}
                        </td>
                        <td className="text-nowrap">
                          {canCurate && (
                            <>
                              <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => handleManageAttributes(itemType)}>
                                Attributes
                              </Button>
                              <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => handleManageGrades(itemType)}>
                                Grades
                              </Button>
                            </>
                          )}
                          <Button size="sm" variant="outline-info" className="me-1" onClick={() => handleViewPrint(itemType, 'ITEM_TYPE')}>View</Button>
                          <Button size="sm" variant="outline-primary" className="me-1" onClick={() => handleEditItemType(itemType)}>Edit</Button>
                          {itemType.organization && (
                            <Button size="sm" variant="outline-success" className="me-1" onClick={() => handleMakeTypeGlobal(itemType)}>Make available to everyone</Button>
                          )}
                          {!itemType.isSystem && canCurate && (
                            <Button size="sm" variant="outline-danger" onClick={() => handleDeleteItemType(itemType.id, itemType.isSystem)}>Delete</Button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Print View Modal */}
      <Modal show={showPrintModal} onHide={() => setShowPrintModal(false)} size="xl" className="print-modal">
        <Modal.Header className="print-header">
          <Modal.Title>
            {printType === 'ITEM_TYPE' && 'Category'}
            {printType === 'ITEM' && 'Item'}
          </Modal.Title>
          <div className="print-actions">
            <Button variant="primary" onClick={handlePrint} className="me-2">Print</Button>
            <Button variant="secondary" onClick={() => setShowPrintModal(false)}>Close</Button>
          </div>
        </Modal.Header>
        <Modal.Body className="print-body">
          <OrgPrintChrome>
          {printType === 'ITEM_TYPE' && printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>CATEGORY</h2>
                <p className="text-muted"><strong>{printData.name}</strong></p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Description:</strong> {printData.description || '-'}</p>
                  <p><strong>Active:</strong> {printData.isActive ? 'Yes' : 'No'}</p>
                </Col>
              </Row>
            </div>
          )}
          {printType === 'ITEM' && printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>ITEM</h2>
                <p className="text-muted"><strong>{printData.name}</strong></p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Category:</strong> {printData.itemType?.name || '-'}</p>
                  <p><strong>GTIN:</strong> {printData.gtin || '-'}</p>
                  <p><strong>HS Code:</strong> {printData.hsCode || '-'}</p>
                  <p><strong>SKU:</strong> {printData.sku || '-'}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Description:</strong> {printData.description || '-'}</p>
                  <p><strong>Active:</strong> {printData.isActive ? 'Yes' : 'No'}</p>
                </Col>
              </Row>
              {Object.keys(printData.attributes || {}).length > 0 && (
                <>
                  <h5 className="mt-3">Attributes</h5>
                  <Row>
                    {Object.entries(printData.attributes || {}).map(([code, value]) => (
                      <Col md={6} key={code}>
                        <p><strong>{code}:</strong> {String(value)}</p>
                      </Col>
                    ))}
                  </Row>
                </>
              )}
            </div>
          )}
                  </OrgPrintChrome>
        </Modal.Body>
      </Modal>

      {/* Category Modal */}
      <Modal show={showItemTypeModal} onHide={() => setShowItemTypeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedItemType ? 'Edit Category' : 'Create Category'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveItemType}>
          <Modal.Body>
            {selectedItemType?.isSystem && (
              <Alert variant="warning">This is a system category. Some fields may be restricted.</Alert>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                value={itemTypeFormData.name}
                onChange={(e) => setItemTypeFormData({ ...itemTypeFormData, name: e.target.value })}
                required
                placeholder="e.g., Raw Material"
                disabled={selectedItemType?.isSystem}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={itemTypeFormData.description}
                onChange={(e) => setItemTypeFormData({ ...itemTypeFormData, description: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Parent Type</Form.Label>
              <Form.Select
                value={itemTypeFormData.parentId}
                onChange={(e) => setItemTypeFormData({ ...itemTypeFormData, parentId: e.target.value })}
              >
                <option value="">— None (top-level) —</option>
                {parentOptionsFor(selectedItemType).map((t) => (
                  <option key={t.id} value={t.id.toString()}>{t.name}</option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Nests this type under a broader group (e.g. an HS Section holding its Chapters).
                Leave blank for a top-level type.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={itemTypeFormData.isActive ? 'true' : 'false'}
                onChange={(e) => setItemTypeFormData({ ...itemTypeFormData, isActive: e.target.value === 'true' })}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Form.Select>
            </Form.Group>
            <Alert variant="light" className="border mb-0">
              Define type-specific fields (Variety, Grade, options, …) with the <strong>Attributes</strong> action on the Categories list.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowItemTypeModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit">{selectedItemType ? 'Update' : 'Create'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Attribute manager for one category */}
      <Modal
        show={showAttrManagerModal}
        onHide={() => setShowAttrManagerModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Attributes for {attrTypeContext?.name || 'Category'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <p className="text-muted mb-0 small">
              These fields appear when creating or editing an Item of this type.
              Each type has its own definitions (e.g. Grade options can differ by type).
            </p>
            <Button variant="primary" size="sm" onClick={handleCreateAttr}>+ Add Attribute</Button>
          </div>
          <Table striped bordered hover size="sm" responsive>
            <thead>
              <tr>
                <th>Order</th>
                <th>Code</th>
                <th>Name</th>
                <th>Type</th>
                <th>Unit / Options</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {typeAttributeDefs.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center text-muted">
                    No attributes yet for this category.
                  </td>
                </tr>
              ) : (
                typeAttributeDefs.map((attr) => (
                  <tr key={attr.id}>
                    <td>{attr.displayOrder}</td>
                    <td><code>{attr.code}</code></td>
                    <td>
                      <strong>{attr.name}</strong>
                      {attr.description && <div className="small text-muted">{attr.description}</div>}
                    </td>
                    <td><Badge bg="secondary">{attr.dataType}</Badge></td>
                    <td className="small">
                      {attr.unit ? `${attr.unit}` : ''}
                      {attr.dataType === 'SELECT'
                        ? `${attr.unit ? ' · ' : ''}${(attr.options || []).join(', ') || '—'}`
                        : (attr.unit ? '' : '—')}
                    </td>
                    <td>
                      {attr.isActive
                        ? <Badge bg="success">Active</Badge>
                        : <Badge bg="danger">Inactive</Badge>}
                    </td>
                    <td className="text-nowrap">
                      <Button size="sm" variant="outline-primary" className="me-1" onClick={() => handleEditAttr(attr)}>Edit</Button>
                      {!attr.isSystem && (
                        <Button size="sm" variant="outline-danger" onClick={() => handleDeleteAttr(attr.id, attr.isSystem)}>Delete</Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAttrManagerModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Attribute definition form */}
      <Modal show={showAttrFormModal} onHide={() => setShowAttrFormModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedAttr ? 'Edit Attribute' : 'Add Attribute'}
            {attrTypeContext ? ` — ${attrTypeContext.name}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveAttr}>
          <Modal.Body>
            {selectedAttr?.isSystem && (
              <Alert variant="warning">System attribute — code cannot be changed.</Alert>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Code *</Form.Label>
              <Form.Control
                type="text"
                value={attrFormData.code}
                onChange={(e) => setAttrFormData({ ...attrFormData, code: e.target.value })}
                required
                placeholder="e.g. variety, grade"
                disabled={selectedAttr?.isSystem}
              />
              <Form.Text className="text-muted">
                Unique within this category. Lowercase letters, digits, underscores.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Display Name *</Form.Label>
              <Form.Control
                type="text"
                value={attrFormData.name}
                onChange={(e) => setAttrFormData({ ...attrFormData, name: e.target.value })}
                required
                placeholder="e.g. Grade"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={attrFormData.description}
                onChange={(e) => setAttrFormData({ ...attrFormData, description: e.target.value })}
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Data Type *</Form.Label>
                  <Form.Select
                    value={attrFormData.dataType}
                    onChange={(e) => setAttrFormData({ ...attrFormData, dataType: e.target.value })}
                  >
                    {ATTR_DATA_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Unit</Form.Label>
                  <Form.Control
                    type="text"
                    value={attrFormData.unit}
                    onChange={(e) => setAttrFormData({ ...attrFormData, unit: e.target.value })}
                    placeholder="%, kg"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Order</Form.Label>
                  <Form.Control
                    type="number"
                    value={attrFormData.displayOrder}
                    onChange={(e) => setAttrFormData({ ...attrFormData, displayOrder: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            {attrFormData.dataType === 'SELECT' && (
              <Form.Group className="mb-3">
                <Form.Label>Options * (one per line)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={attrFormData.optionsText}
                  onChange={(e) => setAttrFormData({ ...attrFormData, optionsText: e.target.value })}
                  placeholder={'Grade A\nGrade B\nGrade C'}
                  required
                />
                <Form.Text className="text-muted">
                  Defined on this category only — other types can use different options for the same name.
                </Form.Text>
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={attrFormData.isActive ? 'true' : 'false'}
                onChange={(e) => setAttrFormData({ ...attrFormData, isActive: e.target.value === 'true' })}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAttrFormModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit">{selectedAttr ? 'Update' : 'Create'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Quality grade manager for one category */}
      <Modal
        show={showGradeManagerModal}
        onHide={() => setShowGradeManagerModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Quality Grades for {gradeTypeContext?.name || 'Category'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <p className="text-muted mb-0 small">
              Grades are stamped onto a lot during quality inspection (e.g. A / B / C).
              Each category has its own grade scale.
            </p>
            <Button variant="primary" size="sm" onClick={handleCreateGrade}>+ Add Grade</Button>
          </div>
          <Table striped bordered hover size="sm" responsive>
            <thead>
              <tr>
                <th>Order</th>
                <th>Code</th>
                <th>Label</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {typeGrades.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center text-muted">
                    No quality grades yet for this category.
                  </td>
                </tr>
              ) : (
                typeGrades.map((grade) => (
                  <tr key={grade.id}>
                    <td>{grade.sortOrder}</td>
                    <td><code>{grade.code}</code></td>
                    <td>
                      <strong>{grade.label}</strong>
                      {grade.description && <div className="small text-muted">{grade.description}</div>}
                    </td>
                    <td>
                      {grade.isActive
                        ? <Badge bg="success">Active</Badge>
                        : <Badge bg="danger">Inactive</Badge>}
                      {grade.isSystem && <Badge bg="warning" className="ms-1">System</Badge>}
                    </td>
                    <td className="text-nowrap">
                      <Button size="sm" variant="outline-primary" className="me-1" onClick={() => handleEditGrade(grade)}>Edit</Button>
                      {!grade.isSystem && (
                        <Button size="sm" variant="outline-danger" onClick={() => handleDeleteGrade(grade.id, grade.isSystem)}>Delete</Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGradeManagerModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Quality grade form */}
      <Modal show={showGradeFormModal} onHide={() => setShowGradeFormModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedGrade ? 'Edit Grade' : 'Add Grade'}
            {gradeTypeContext ? ` — ${gradeTypeContext.name}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveGrade}>
          <Modal.Body>
            {selectedGrade?.isSystem && (
              <Alert variant="warning">System grade — code cannot be changed.</Alert>
            )}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Code *</Form.Label>
                  <Form.Control
                    type="text"
                    value={gradeFormData.code}
                    onChange={(e) => setGradeFormData({ ...gradeFormData, code: e.target.value })}
                    required
                    placeholder="e.g. A, B, PREMIUM"
                    disabled={selectedGrade?.isSystem}
                  />
                  <Form.Text className="text-muted">
                    Unique within this category. Uppercase letters, digits, underscores.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Order</Form.Label>
                  <Form.Control
                    type="number"
                    value={gradeFormData.sortOrder}
                    onChange={(e) => setGradeFormData({ ...gradeFormData, sortOrder: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Label *</Form.Label>
              <Form.Control
                type="text"
                value={gradeFormData.label}
                onChange={(e) => setGradeFormData({ ...gradeFormData, label: e.target.value })}
                required
                placeholder="e.g. Grade A"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={gradeFormData.description}
                onChange={(e) => setGradeFormData({ ...gradeFormData, description: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={gradeFormData.isActive ? 'true' : 'false'}
                onChange={(e) => setGradeFormData({ ...gradeFormData, isActive: e.target.value === 'true' })}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowGradeFormModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit">{selectedGrade ? 'Update' : 'Create'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Item Modal */}
      <Modal show={showItemModal} onHide={() => setShowItemModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedItem ? 'Edit Item' : 'Create Item'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveItem}>
          <Modal.Body>
            {selectedItem?.isSystem && (
              <Alert variant="warning">This is a system item. Some fields may be restricted.</Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                value={itemFormData.name}
                onChange={(e) => setItemFormData({ ...itemFormData, name: e.target.value })}
                required
                placeholder="e.g., Steel Sheet"
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>GTIN</Form.Label>
                  <Form.Control
                    type="text"
                    value={itemFormData.gtin}
                    onChange={(e) => setItemFormData({ ...itemFormData, gtin: e.target.value })}
                    maxLength={50}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>HS Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={itemFormData.hsCode}
                    onChange={(e) => setItemFormData({ ...itemFormData, hsCode: e.target.value })}
                    maxLength={50}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>SKU</Form.Label>
                  <Form.Control
                    type="text"
                    value={itemFormData.sku}
                    onChange={(e) => setItemFormData({ ...itemFormData, sku: e.target.value })}
                    maxLength={100}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Category *</Form.Label>
              <SearchableSelect
                options={typeOptions(itemTypes.filter((it) => it.isActive))}
                value={itemFormData.itemTypeId}
                onChange={(typeId) => applyItemTypeChange(typeId, itemFormData.attributes)}
                required
                placeholder="Type to search categories…"
                emptyLabel="No matching category"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={itemFormData.description}
                onChange={(e) => setItemFormData({ ...itemFormData, description: e.target.value })}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={itemFormData.isActive ? 'true' : 'false'}
                onChange={(e) => setItemFormData({ ...itemFormData, isActive: e.target.value === 'true' })}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Form.Select>
            </Form.Group>

            <hr />
            <h6 className="mb-3">
              Attributes
              {itemFormData.itemTypeId && (
                <span className="text-muted fw-normal small">
                  {' '}(from selected category)
                </span>
              )}
            </h6>
            {!itemFormData.itemTypeId ? (
              <Alert variant="light" className="border">Select a category to see its attributes.</Alert>
            ) : itemTypeAttributes.length === 0 ? (
              <Alert variant="light" className="border">
                This category has no active attributes. Add them under Categories → Attributes.
              </Alert>
            ) : (
              <Row>
                {itemTypeAttributes.map((def) => (
                  <Col md={6} key={def.code}>
                    {renderAttributeInput(def)}
                  </Col>
                ))}
              </Row>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowItemModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit">{selectedItem ? 'Update' : 'Create'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Verify Item Modal */}
      <Modal show={showVerifyModal} onHide={() => setShowVerifyModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Approve "{verifyTargetItem?.name}"</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveVerify}>
          <Modal.Body>
            <p className="text-muted small">
              Fill in the details an org admin creating this draft wouldn't reliably know, then approve it.
              It stays private to the organization that made it; use "Make available to everyone" to share
              it instead. If it is a duplicate of an existing item, use "Merge into…".
            </p>
            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              {/* The shared categories, and this organization's own: never another organization's */}
              <SearchableSelect
                options={typeOptions(itemTypes.filter((t) => t.isActive
                  && (!t.organization || t.organization.id === verifyTargetItem?.createdByOrganization?.id)))}
                value={verifyFormData.itemTypeId}
                onChange={(typeId) => setVerifyFormData({ ...verifyFormData, itemTypeId: typeId })}
                required
                placeholder="Type to search categories…"
                emptyLabel="No matching category"
              />
              <Form.Text className="text-muted">
                Move it to the right category if they chose the wrong one. Specification values the new category has no attribute for are dropped.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>GTIN</Form.Label>
              <Form.Control
                type="text"
                value={verifyFormData.gtin}
                onChange={(e) => setVerifyFormData({ ...verifyFormData, gtin: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>HS Code</Form.Label>
              <Form.Control
                type="text"
                value={verifyFormData.hsCode}
                onChange={(e) => setVerifyFormData({ ...verifyFormData, hsCode: e.target.value })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowVerifyModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={savingVerify}>
              {savingVerify ? 'Approving...' : 'Approve, keep private'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Merge Item Modal */}
      <Modal show={showTypeOfModal} onHide={() => setShowTypeOfModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>File "{typeOfTarget?.name}" under a parent item</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveTypeOf}>
          <Modal.Body>
            <p className="text-muted small">
              For a size, brand or model of a parent item (Round Pad Lock, 50mm goes under Padlock). It is approved, moved to the parent item's category if it is in another, stays its own material
              with its own stock and cost, private to {typeOfTarget?.createdByOrganization?.name || 'the organization'}, and is filed under the
              parent item you pick. The Org Admin is told.
            </p>
            <Form.Group className="mb-3">
              <Form.Label>Parent item *</Form.Label>
              <SearchableSelect
                options={items
                  .filter((i) => i.verified !== false && !i.genericItemId && i.isActive && i.id !== typeOfTarget?.id
                    && (!i.createdByOrganization || i.createdByOrganization.id === typeOfTarget?.createdByOrganization?.id))
                  .map((i) => ({
                    value: String(i.id),
                    label: i.name,
                    subtitle: [i.itemType?.name, i.createdByOrganization?.name ? `private to ${i.createdByOrganization.name}` : 'shared'].filter(Boolean).join(' · '),
                    keywords: `${i.hsCode || ''} ${i.description || ''}`
                  }))}
                value={typeOfGenericId}
                onChange={setTypeOfGenericId}
                required
                placeholder="Type to search, for example Padlock"
                emptyLabel="No matching parent item"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowTypeOfModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={savingTypeOf || !typeOfGenericId}>
              {savingTypeOf ? 'Saving...' : 'Approve and file'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showMergeModal} onHide={() => setShowMergeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Merge "{mergeTargetItem?.name}" into…</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveMerge}>
          <Modal.Body>
            <p className="text-muted small">
              Every reference to "{mergeTargetItem?.name}" (org materials, GRNs, GINs, BOMs, products,
              and more) will be reassigned to the item you pick below, and this draft will be deleted.
              This cannot be undone.
            </p>
            <Form.Group className="mb-3">
              <Form.Label>Canonical item *</Form.Label>
              <SearchableSelect
                options={items
                  .filter((i) => i.verified !== false && i.id !== mergeTargetItem?.id)
                  .map((i) => ({
                    value: String(i.id),
                    label: i.name,
                    subtitle: [i.itemType?.name, i.hsCode ? `HSN ${i.hsCode}` : null, i.createdByOrganization?.name ? `private to ${i.createdByOrganization.name}` : 'shared'].filter(Boolean).join(' · '),
                    keywords: `${i.sku || ''} ${i.hsCode || ''} ${i.description || ''}`
                  }))}
                value={mergeCanonicalId}
                onChange={setMergeCanonicalId}
                required
                placeholder="Type to search for the item to keep…"
                emptyLabel="No matching item"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowMergeModal(false)}>Cancel</Button>
            <Button variant="danger" type="submit" disabled={savingMerge || !mergeCanonicalId}>
              {savingMerge ? 'Merging...' : 'Merge'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}

export default MaterialMaster;
